import { BehaviorSubject, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { catchError, mergeMap, tap } from 'rxjs/operators';

import { C } from 'src/app/@shared/constants';

import { ClientFilterObject } from '@common/filter.decorator';
import { DEBUG_COLORS } from 'src/app/@shared/base.page';
import { HttpErrorResponse } from '@angular/common/http';

export type ListIdWithItemIndex = { id: string; itemIndex: number };

export type ContentOptions<T> = {
  type?: string; // 'list', 'detail', etc. -> defaults to list if not set
  forceReload?: boolean;
  filter?: ClientFilterObject<T>;
  withoutAuthentication?: boolean;
};

export type MetaDataStore<U> = {
  totalCount: number;
  appliedFilter: ClientFilterObject<U>;
  error?: HttpErrorResponse;
};

@Injectable({
  providedIn: 'root',
})
export class ContentService<T extends { id: string }, U> {
  protected items: { [key in string]: BehaviorSubject<T[]> } = {};
  protected itemsStore: { [key in string]: T[] } = {};
  protected metadataStore: { [key in string]?: Partial<MetaDataStore<U>> } = {};

  // returns observable of array
  // array contains 0..n items for list view and 0..1 item for detail view
  public getContent(id: string, options: ContentOptions<U> = {}): Observable<T[] | null> {
    const hasLoadedContent = this.items[id] && this.items[id].getValue() !== null;

    if (hasLoadedContent && !options.forceReload) {
      return this.items[id].asObservable();
    }

    if (!this.items[id]) {
      this.items[id] = new BehaviorSubject(null);
    }

    return this.loadContent(id, options).pipe(tap(() => {
      if (this.metadataStore[id]) {
        delete this.metadataStore[id].error;
      }
    }),
    catchError((error) => {
      if (!this.metadataStore[id]) {
        this.metadataStore[id] = {};
      }

      this.metadataStore[id].error = error;

      return of(null);
    }),
    mergeMap(() => { return this.items[id].asObservable(); }));
  }

  public loadContent(id: string, options: ContentOptions<U> = {}): Observable<T[]> {
    const consoleMessage = '%c[ContentService] loadContent(id, options) missing in "%s"';

    console.info(
      consoleMessage, `color: ${DEBUG_COLORS.WARN}`, this.constructor.name, id, options,
    );

    return of([]);
  }

  public getMetaData(id: string): Partial<MetaDataStore<U>> {
    return this.metadataStore[id] || {};
  }

  public hasMoreItems(listId: string): boolean {
    if (!this.itemsStore[listId] || !this.metadataStore[listId]) {
      return false;
    }

    return this.itemsStore[listId].length < this.metadataStore[listId].totalCount;
  }

  public removeItem(id: string, listId?: string): void {
    // if list id is defined, only remove item from this list (e.g. when user removes bookmark)
    if (listId) {
      if (this.itemsStore[listId]) {
        console.info('[ContentService] remove item from store', listId);
        this.itemsStore[listId] = this.itemsStore[listId].filter((item) => { return item.id !== id; });
        this.metadataStore[listId].totalCount -= 1;
        this.updateItemsBehaviorSubjects([listId]);
      }

      return;
    }

    // remove item from single item store (detail) + complete subscription
    if (this.items[id]) {
      console.info('[ContentService] remove item from detail', id);
      this.items[id].complete();
      this.items[id] = null;
      this.itemsStore[id] = [];
    }

    // if list id is not defined, remove item from all existing lists (e.g. when user deletes item)
    // we can exclude this.itemsStore[id] here because we already handled it before
    const listsWithItem = this.getListIdsWithItemIndex(id, [id]);

    for (const list of listsWithItem) {
      console.info('[ContentService] remove item from list', list.id, 'item position', list.itemIndex);
      this.itemsStore[list.id] = this.itemsStore[list.id].filter((item) => { return item.id !== id; });
      this.metadataStore[list.id].totalCount -= 1;
      this.updateItemsBehaviorSubjects([list.id]);
    }
  }

  public getStore(storeId: string): T[] | null {
    return this.itemsStore[storeId];
  }

  public getStoreItem(itemId: string): T | null {
    if (this.itemsStore[itemId]?.length) {
      return this.itemsStore[itemId][0];
    }

    let item: T;

    // search in all other stores
    for (const id of Object.keys(this.itemsStore)) {
      const listIndex = this.itemsStore[id].findIndex((storedItem) => { return storedItem.id === itemId; });

      if (listIndex !== -1) {
        item = this.itemsStore[id][listIndex];
      }
    }

    return item || null;
  }

  // TODO: move to static methods
  protected getNormalizedFilter(id: string, _filter: ClientFilterObject<U> = {}): ClientFilterObject<U> {
    const filter = {
      order: _filter.order || C.DEFAULT_ORDER_BY,
      relations: _filter.relations || null,
      skip: _filter.skip || 0,
      take: _filter.take || C.DEFAULT_LIMIT,
      where: _filter.where || {},
    };

    return filter;
  }

  // set items when loading items has been succeeded
  protected setItems(items: T[], listId: string, filter: ClientFilterObject<U>, totalCount: number): void {
    // Reset for pull to refresh, etc.
    if (!this.itemsStore[listId] || filter.skip === 0) {
      this.itemsStore[listId] = [];
    }

    this.metadataStore[listId] = {
      appliedFilter: filter,
      totalCount: totalCount || items.length,
    };

    for (const item of items) {
      this.updateItemInStore(item, listId);
    }

    this.updateItemsBehaviorSubjects([listId]);
  }

  protected getListIdsWithItemIndex(id: string, excludedLists: string[] = []): ListIdWithItemIndex[] {
    const lists: ListIdWithItemIndex[] = [];

    for (const listId of Object.keys(this.itemsStore)) {
      const index = this.getItemIndexByListId(listId, id);

      if (index !== -1 && !excludedLists.includes(listId)) {
        lists.push({
          id: listId,
          itemIndex: index,
        });
      }
    }

    return lists;
  }

  protected updateItem(item: T, listId?: string, prependItem?: boolean): void {
    if (!item || !item.id) {
      console.error('[ContentService] item id required', item);

      return;
    }

    // update/init detail store
    if (this.itemsStore[item.id]?.length) {
      // replace reference while preserving model prototype
      this.itemsStore[item.id][0] = this.mergeItem(this.itemsStore[item.id][0], item);
    } else {
      this.itemsStore[item.id] = [item];
    }

    if (listId) {
      this.updateItemInStore(item, listId, prependItem);
    }

    const updatedListIds = this.updateItemInAllStores(item, [item.id, listId]);

    this.updateItemsBehaviorSubjects([item.id, listId, ...updatedListIds]);
  }

  // add or update item in store
  protected updateItemInStore(item: T, listId: string, prependItem?: boolean): void {
    const index = this.getItemIndexByListId(listId, item.id);

    // update item in store
    if (index !== -1) {
      this.itemsStore[listId][index] = this.mergeItem(this.itemsStore[listId][index], item);

      return;
    }

    // add item if store is available
    if (!this.itemsStore[listId]) {
      return;
    }

    if (prependItem) {
      this.itemsStore[listId].unshift(item);
    } else {
      this.itemsStore[listId].push(item);
    }
  }

  // update all existing stores where current item is featured
  protected updateItemInAllStores(item: T, excludedListIds: string[] = []): string[] {
    const listsWithItem = this.getListIdsWithItemIndex(item.id, excludedListIds);
    const updatedListIds: string[] = [];

    for (const list of listsWithItem) {
      this.itemsStore[list.id][list.itemIndex] = this.mergeItem(this.itemsStore[list.id][list.itemIndex], item);
      updatedListIds.push(list.id);
    }

    return updatedListIds;
  }

  protected updateItemsBehaviorSubjects(storeIds: string[]): void {
    // update all available behavior subjects
    for (const id of storeIds) {
      if (id && this.items[id]) {
        console.info('[ContentService] updateItemsBehaviorSubjects', id);

        // !!! slice is required here, otherwise we run into call by reference issues
        this.items[id].next(this.itemsStore[id].slice());
      }
    }
  }

  private getItemIndexByListId(listId: string, id: string): number {
    if (!this.itemsStore[listId]) {
      return -1;
    }

    return this.itemsStore[listId].findIndex((storedItem) => { return storedItem.id === id; });
  }

  private mergeItem(existingItem: T, incomingItem: T): T {
    const mergedItem = Object.create(Object.getPrototypeOf(existingItem)) as T;

    Object.assign(mergedItem, existingItem, incomingItem);

    return mergedItem;
  }
}
